<script module>
  import { gql } from '$lib/graphql.svelte.js'

  export const i18n = {
    en: {
      YOUTH: 'youth',
      SENIOR: 'senior',
      ADULT: 'adult',
      '%s ADULT': {
        one: '%s adult',
        other: '%s adults'
      },
      '%s SENIORR': {
        one: '%s senior',
        other: '%s seniors'
      },
      '%s YOUTH': {
        one: '%s youth',
        other: '%s youths'
      },
      '%s people': {
        one: '%s person',
        other: '%s people'
      },
      '%s nights in': {
        one: 'Spend a night in',
        other: 'Spend %s nights in'
      },
      '%s changes': {
        one: '%s change',
        other: '%s changes'
      },
      '%s days': {
        one: '%s day',
        other: '%s days'
      }
    },
    sv: {
      YOUTH: 'ungdom',
      SENIOR: 'senior',
      ADULT: 'vuxen',
      '%s ADULT': {
        one: '%s vuxen',
        other: '%s vuxna'
      },
      '%s SENIOR': {
        one: '%s senior',
        other: '%s seniorer'
      },
      '%s YOUTH': {
        one: '%s ungdom',
        other: '%s ungdomar'
      },
      '%s people': {
        one: '%s person',
        other: '%s personer'
      },
      '%s nights in': {
        one: 'En natt i',
        other: '%s nätter i'
      },
      '%s changes': {
        one: '%s byte',
        other: '%s byten'
      },
      '%s days': {
        one: '%s dag',
        other: '%s dagar'
      },
      Select: 'Välj',
      '2 class': '2 klass',
      '1 class': '1 klass',
      Selected: 'Vald',
      Unavailable: 'Ej tillgänglig',
      'Price from': 'Pris från',
      'Find tickets': 'Hitta biljetter',
      'Loading tickets': 'Söker biljetter'
    }
  }

  export const JourneyFragment = gql`
    fragment journey on Journey {
      __typename
      id
      status
      itinerary {
        __typename
        ... on Stopover {
          duration
          location {
            uid
            name
            countryCode
            coordinates {
              type
              coordinates
            }
            image {
              url
              alt
            }
          }
        }
        ... on SegmentCollection {
          status
          segments {
            __typename
            duration
            departureAt
            arrivalAt
            transport
            identifier
            direction
            services
            operator {
              name
              image {
                url
              }
            }
            serviceBrand {
              name
              image {
                url
              }
            }
            origin {
              name
              ... on Location {
                uid
                countryCode
              }
            }
            destination {
              name
              ... on Location {
                uid
                countryCode
              }
            }
          }
        }
      }
    }
  `

  export const JourneyWithAvailabilityFragment = gql`
    fragment journeyWithAvailability on Journey {
      ...${JourneyFragment}
      itinerary {
        ... on SegmentCollection {
          availability {
            status
            ticketClasses {
              serviceClass
            }
            priceFrom {
              amount
              currency
            }
          }
        }
      }
    }
  `

  export const JourneyOfferFragment = gql`
    fragment journeyOffer on JourneyOffer {
      ...${JourneyFragment}
      itinerary {
        ... on SegmentCollection {
          offers {
            parts {
              __typename
              ... on AdmissionPart {
                serviceClass
              }
            }
            price {
              amount
              currency
            }
          }
        }
      }
    }
  `

  /** @param {Schema.JourneyOffer?} [journey] */
  export function getCheapestPrice(
    journey,
    serviceClasses = ['BASIC', 'STANDARD', 'HIGH', 'BEST', 'UNKNOWN']
  ) {
    if (journey?.status !== 'SUCCESS' || !journey.itinerary) return null

    const offers = journey.itinerary
      .filter((item) => item.__typename === 'SegmentCollection')
      .map((/** @type {Schema.SegmentCollection} */ item) =>
        item.offers?.filter((offer) =>
          offer.parts.every(
            (part) =>
              part.__typename !== 'AdmissionPart' ||
              serviceClasses.includes(/** @type {string} */ (part.serviceClass))
          )
        )
      )
      .reduce((acc, offers) => {
        if (!acc || !offers?.length) return undefined

        acc.push(
          offers.reduce((cheapest, offer) => {
            return offer.price.amount < cheapest.price.amount ? offer : cheapest
          })
        )

        return acc
      }, /** @type {Schema.Offer[]} */ ([]))

    if (!offers?.length) return null

    return offers.reduce(
      (price, offer) => ({
        ...offer.price,
        amount: offer.price.amount + price.amount
      }),
      /** @type {Schema.Money} */ ({ amount: 0 })
    )
  }
</script>

<script>
  import JourneyDetails from '$lib/JourneyDetails.svelte'
  import { register } from '$lib/utils/text.svelte.js'
  import { money } from '$lib/utils/money.svelte.js'
  import State from '$lib/utils/state.svelte.js'
  import Symbol from '$lib/Symbol.svelte'
  import { slide } from 'svelte/transition'

  let text = register(i18n)

  /** @type {{
   * journey?: Schema.Journey?,
   * origin?: Schema.Location?,
   * destination?: Schema.Location?,
   * passengers?: Schema.PassengerPlaceholderInput[],
   * isPassholder?: boolean,
   * withOffer?: boolean,
   * selected?: boolean,
   * disabled?: boolean,
   * action?: Svelte.Snippet<[Schema.Journey, App.GraphQLOperationResult<{ journey: Schema.JourneyOffer }>, { selected: boolean }]>,
   * onselect?: function(Schema.JourneyOffer): void
   * }}*/
  let {
    journey = null,
    isPassholder = false,
    selected = false,
    withOffer = false,
    passengers,
    disabled,
    action,
    onselect
  } = $props()

  const FIRST_CLASS_SERVICES = ['HIGH', 'BEST']
  const SECOND_CLASS_SERVICES = ['BASIC', 'STANDARD']

  /** @type {App.GraphQLOperation<{ journey: Schema.JourneyOffer }, { id: string, currency: string, passengers: Schema.PassengerPlaceholderInput[], isPassholder: boolean }>} */
  const GetJourneyOffer = gql`
    subscription GetJourneyOffer(
      $id: ID!
      $currency: String
      $passengers: [PassengerPlaceholderInput!]!
      $isPassholder: Boolean = false
    ) {
      journey: getJourneyOffer(
        journey: $id
        currency: $currency
        passengers: $passengers
        isPassholder: $isPassholder
      ) {
        ...${JourneyOfferFragment}
      }
    }
  `

  let getOffer = $state(withOffer)

  let itinerary = $derived.by(() => {
    const hasCompleteItinerary =
      GetJourneyOffer.data?.journey?.itinerary?.every(
        (item) => item.__typename !== 'SegmentCollection' || item.segments
      )

    return hasCompleteItinerary
      ? GetJourneyOffer.data?.journey?.itinerary
      : journey?.itinerary?.map((item) => {
          if (item.__typename !== 'SegmentCollection') return item
          const status = GetJourneyOffer.fetching ? 'LOADING' : item.status
          return { ...item, status }
        })
  })

  // Since the entire journey object may change we need to pluck properties that are tracked
  let journeyId = $derived(journey?.id)

  $effect(() => {
    if (journeyId && getOffer && passengers) {
      return GetJourneyOffer.execute({
        currency: State.currency,
        id: journeyId,
        passengers,
        isPassholder
      })
    }
  })

  let isSoldOut = $derived(
    itinerary
      ?.filter((item) => item.__typename === 'SegmentCollection')
      .map((/** @type {Schema.SegmentCollection} */ item) => item.availability)
      .some((availability) => availability?.status === 'SOLD_OUT')
  )

  let priceFrom = $derived(
    itinerary
      ?.filter((item) => item.__typename === 'SegmentCollection')
      .reduce((acc, item, index) => {
        if (!acc && index) return null

        const { availability } = /** @type {Schema.SegmentCollection} */ (item)
        if (!availability?.priceFrom) return null

        const amount = acc ? acc.amount : 0
        return {
          ...availability.priceFrom,
          amount: availability.priceFrom.amount + amount
        }
      }, /** @type {Schema.Money?} */ (null))
  )
</script>

{#snippet sidebar(
  /** @type {Schema.Journey?} */ journey,
  /** @type {Schema.JourneyOffer|undefined} */ journeyOffer
)}
  {@const secondClassPrice = getCheapestPrice(
    journeyOffer,
    SECOND_CLASS_SERVICES
  )}
  {@const firstClassPrice = getCheapestPrice(
    journeyOffer,
    FIRST_CLASS_SERVICES
  )}
  {@const unknownClassPrice = getCheapestPrice(journeyOffer, ['UNKNOWN'])}
  {@const ticketClasses = itinerary
    ?.filter((item) => item.__typename === 'SegmentCollection')
    ?.flatMap(
      (/** @type {Schema.SegmentCollection} */ item) =>
        item.availability?.ticketClasses
    )}
  {@const secondClassAvailability =
    Boolean(secondClassPrice) ||
    !ticketClasses?.length ||
    ticketClasses?.some(
      (ticketClass) =>
        !ticketClass || SECOND_CLASS_SERVICES.includes(ticketClass.serviceClass)
    )}
  {@const firstClassAvailability =
    Boolean(firstClassPrice) ||
    !ticketClasses?.length ||
    ticketClasses?.some(
      (ticketClass) =>
        !ticketClass || FIRST_CLASS_SERVICES.includes(ticketClass.serviceClass)
    )}
  {@const unavailable = isSoldOut || journeyOffer?.status === 'ERROR'}

  {#snippet classPrice(price)}
    {#if unavailable}
      <span class="label muted">{text`Unavailable`}</span>
    {:else if price}
      <span class="price">{money(price)}</span>
    {/if}
  {/snippet}

  {#snippet uncertainClassPrice(price)}
    <div class="shared">
      <span class="label">{text`Price from`}</span>
      <span class="price">{money(price)}</span>
    </div>
  {/snippet}

  <div class="sidebar" class:unavailable>
    <div class="proposal">
      <div class="classes">
        <div class="class">
          <span
            class="label"
            class:muted={!secondClassPrice && !secondClassAvailability}>
            {text`2 class`}
          </span>
          {@render classPrice(secondClassPrice)}
        </div>
        <div class="class">
          <span
            class="label"
            class:muted={!firstClassPrice && !firstClassAvailability}>
            {text`1 class`}
          </span>
          {@render classPrice(firstClassPrice)}
        </div>
      </div>
      {#if priceFrom && !secondClassPrice && !firstClassPrice}
        {@render uncertainClassPrice(priceFrom)}
      {/if}
      {#if !secondClassPrice && !firstClassPrice && unknownClassPrice}
        {@render uncertainClassPrice(unknownClassPrice)}
      {/if}
    </div>
    {#if !unavailable && journey && itinerary}
      <button
        type="submit"
        class="action"
        class:primary={journeyOffer?.status === 'SUCCESS'}
        disabled={Boolean(
          disabled ||
            GetJourneyOffer.error ||
            GetJourneyOffer.fetching ||
            journeyOffer?.status === 'ERROR'
        )}
        name={journeyOffer ? 'journeyOfferId' : 'journeyId'}
        value={journeyOffer?.id || journey.id}
        onclick={() => {
          if (!getOffer) {
            getOffer = true
          } else if (
            journeyOffer?.status === 'SUCCESS' &&
            typeof onselect === 'function'
          ) {
            onselect(journeyOffer)
          }
        }}>
        {#if action}
          {@render action(journey, GetJourneyOffer, { selected })}
        {:else if selected}
          {text`Selected`}
        {:else if journeyOffer?.status === 'SUCCESS'}
          {text`Select`}
        {:else if GetJourneyOffer.fetching}
          {text`Loading tickets`}
          <span class="figure" in:slide={{ duration: 200, axis: 'x' }}>
            <Symbol name="spinner" size="fill" />
          </span>
        {:else}
          {text`Find tickets`}
        {/if}
      </button>
    {/if}
  </div>
{/snippet}

<article class="journey" class:selected data-journey={journey?.id}>
  <div class="container">
    <JourneyDetails
      {itinerary}
      showStatus={Boolean(GetJourneyOffer.data?.journey) ||
        GetJourneyOffer.fetching} />
    {@render sidebar(journey, GetJourneyOffer.data?.journey)}
  </div>
</article>

<style>
  .journey {
    container-type: inline-size;
    position: relative;
    color: rgb(var(--rgb-text));
    transition: var(--box-shadow-transition);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--box-shadow-md);
    font-size: 1rem;
  }

  .journey.selected::after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    pointer-events: none;
    border: 3px solid rgb(var(--rgb-primary, var(--rgb-bright-green)));
    border-radius: var(--border-radius-lg);
  }

  .container {
    display: flex;
    flex-direction: column;
  }

  @container (width >= 35rem) {
    .container {
      display: flex;
      flex-direction: row;
    }
  }

  .sidebar {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.75rem;
    padding: 1rem;
    position: relative;
    background: var(--background);
    border-top: 2px solid rgba(var(--rgb-muted), 0.2);
    font-size: 0.9375rem;
    border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  }

  @container (width >= 35rem) {
    .sidebar {
      display: block;
      flex: 0 0 13rem;
      border-radius: 0 var(--border-radius-lg) var(--border-radius-lg) 0;
      border-top: 0;
      border-left: 1px solid rgba(var(--rgb-muted), 0.2);
    }
  }

  @container (width >= 45rem) {
    .sidebar {
      flex: 0 0 16rem;
    }
  }

  .sidebar.unavailable {
    display: grid;
    gap: 1.5rem;
    padding: 1rem;
    font-size: 0.875rem;
    grid-template-columns: auto;
  }

  .proposal {
    width: 100%;
    display: flex;
    align-items: baseline;
  }

  .classes {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  @container (width >= 35rem) {
    .classes {
      margin-bottom: 0.625rem;
    }
  }

  .class {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 1.25rem;
    margin-bottom: 0.25rem;
  }

  .class + .class {
    border-top: 1px solid rgba(var(--rgb-muted), 0.2);
    margin: 0;
    padding-top: 0.25rem;
    height: calc(1.25rem + 0.25rem);
  }

  .label {
    font-weight: 500;
    font-size: 0.8125rem;
    line-height: 1;
    white-space: nowrap;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .label.muted {
    color: rgba(var(--rgb-muted), 0.8);
  }

  .shared {
    display: flex;
    flex-wrap: wrap;
    justify-content: end;
    gap: 0.25rem;
    padding-left: 2rem;
  }

  .price {
    font-weight: 700;
    font-size: 1.125rem;
    line-height: 1.1;
    text-align: right;
    letter-spacing: -0.03em;
    height: 1.25rem;
  }

  /** Actions */

  .action {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    border: transparent;
    background: transparent;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    cursor: pointer;
    user-select: none;
    font-weight: 600;
    font-size: 1rem;
    border-radius: var(--border-radius-full);
    transition: var(--box-shadow-transition);
    box-shadow: var(--box-shadow-sm);
  }

  @container (width >= 35rem) {
    .action {
      padding: 0.5rem 1rem;
      height: 2.375rem;
    }
  }

  .action:not(:disabled):hover {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .action:not(:disabled):active {
    box-shadow: var(--box-shadow-sm-active);
    transition: var(--box-shadow-active-transition);
  }

  .action.primary {
    background: rgb(var(--rgb-primary, var(--rgb-green)));
    color: rgb(var(--rgb-white));
    font-weight: 600;
  }

  .journey.selected .action {
    background: rgb(var(--rgb-primary, var(--rgb-green)));
    color: rgb(var(--rgb-white));
  }

  .action:disabled:not(.primary) {
    background: transparent;
    color: rgba(var(--rgb-muted));
    border: 1px solid rgba(var(--rgb-muted), 0.25);
    cursor: default;
    font-weight: 400;
    box-shadow: none;
  }

  .action .figure {
    width: 1rem;
    height: 1rem;
    margin-left: 0.5rem;
  }

  @container (width >= 35rem) {
    .action .figure {
      display: none;
    }
  }
</style>
